import React, { useContext } from 'react';
import { Autocomplete, Button, Divider, FormControl, OutlinedInput, InputLabel, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import { AlertProps } from '@mui/material/Alert';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate, useParams } from 'react-router-dom';

import { Modal } from './Modal';
import { httpGet, httpPost } from '../Web';
import dayjs from 'dayjs';
import { UserContext } from '../UserProvider';
import { useSnackbar } from '../../contexts/SnackBarContext';

export interface WorkshopSettings {
    id?: number,
    guid?: string,
    name?: string,
    startDate?: string,
    endDate?: string,
    location?: string,
    contactEmail?: string,
    status?: number,
    fee?: string,
    paymentType?: number,
    maxUsers?: number
    creatorId?: number,
    informationUrl?: string,
    interfaces?: Interface[],
}

interface WorkshopSettingsModalProps {
    title: string
    id?: string,
    workshopSettings?: WorkshopSettings,
    selectedInterfaces?: Interface[],
    workshopCreator?: string,
    submitEventHandler?: any,
    showUserFeedback: (feedback: string) => void
}

interface Interface {
    id: number,
    name: string,
    shortName: string,
    status: InterfaceStatus
}

interface WorkshopInterface {
    workshopId: number,
    interfaceId: number,
}

enum InterfaceStatus {
    Draft = 0, Released = 1, Disabled = 2
}

function Interfaces2WorkshopInterfaces(interfaces: Interface[], workshopId: number): WorkshopInterface[] {
    return interfaces.map( iface => { return { workshopId: workshopId, interfaceId: iface.id } });
}

function InterfaceSearchAutocomplete({wsId, available, selected, setNewSelected}: {wsId: number, available: Interface[], selected: WorkshopInterface[], setNewSelected: (value: WorkshopInterface[])=> void}) {
    let selectedIds = selected.map(wi => wi.interfaceId);

    return (
    <FormControl fullWidth>
        <Autocomplete id='ws-interfaces'
            fullWidth
            multiple
            isOptionEqualToValue={(option, value) => option.id === value.id}
            options={available}
            value={selectedIds.map(id => available[available.findIndex(value => value.id === id)])}
            getOptionLabel={iface => iface.shortName}
            onChange={(event, value) => { console.log('selected ifaces: '+JSON.stringify(selected));  setNewSelected(Interfaces2WorkshopInterfaces(value, wsId));}}
            renderInput={params =>
                <TextField
                    label="Interfaces"
                    {...params}
                    variant='outlined'
                    placeholder='search for interfaces'
                    slotProps={{
                        inputLabel: {
                            shrink: true
                        },
                    }}
                />
            }
        />
    </FormControl>)
}

export const WorkshopSettingsModal = () => {
    const { id: idOrNew } = useParams();
    const userContext = React.useContext(UserContext);
    const { showSnackbar } = useSnackbar();

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    const updateInterfaces = (selectedInterfaces: WorkshopInterface[], oldselectedInterfaces: WorkshopInterface[], workshopId: number) => {
        let workshopInterfaces = selectedInterfaces.map(iface => { return  {workshopId: workshopId, interfaceId: iface.interfaceId}});
        console.log('ws interfaces: ', workshopInterfaces);

        // use oldselectedInterfaces to find interfaces that have been removed and delete them
        let removedInterfaces = oldselectedInterfaces.filter(oldIface => !selectedInterfaces.find(newIface => newIface.interfaceId === oldIface.interfaceId));
        console.log('removed interfaces: ', removedInterfaces);
        let deletePromises = removedInterfaces.map(ri => httpPost(`api/workshop/interface/delete`, ri, new AbortController()));
        // wait for all delete promises to complete
        Promise.all(deletePromises).then(() => {
            console.log('deleted removed interfaces');
        });

        // now update or add the selected interfaces
        let workshopInterfacePromises = workshopInterfaces.map(wi => httpPost(`api/workshop/interface/update`, wi, new AbortController()));

        return Promise.all(workshopInterfacePromises)
    }

    const submitEventHandler = (newWorkshopSettings: WorkshopSettings, selectedInterfaces: WorkshopInterface[], oldselectedInterfaces: WorkshopInterface[]) =>
        (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        let result = httpPost(`/api/workshop/update`, newWorkshopSettings, new AbortController());


        result.then((successValue)=>{ 
            if(successValue.failed) {
                showUserFeedback(successValue.errorText);
                console.info(`error: ${successValue.errorCode} ${successValue.errorText}`);
            }
            else if(idOrNew === 'new') {
                const newWorkshopReadback = successValue.result;
                const newWorkshopId = newWorkshopReadback.id;

                console.log('New workshopId: ', newWorkshopId);

                httpPost(`/api/workshop/user/update`, {userId: userContext.user.id as number, workshopId: newWorkshopReadback.id, isWorkshopAdmin: true}, new AbortController())
                    .then(result => { console.log('new user: ', result); updateInterfaces(selectedInterfaces, [], newWorkshopId); });

                successValue.failed = false;
                showUserFeedback(successValue.result.name + " has been created.", 'success');
            }
            else {
                console.log('Update interfaces: ', idOrNew)
                updateInterfaces(selectedInterfaces, oldselectedInterfaces, Number.parseInt(idOrNew as string));

                successValue.failed = false;
                showUserFeedback(successValue.result.name + " has been updated.", 'success');

            }
   
            console.info('success value: ', successValue);
        },
            (errorValue)=>{ console.info(`failed: ${errorValue}`) 
        });

        console.log('event: ', newWorkshopSettings);
        console.log('RESULT: ', result);
    }

    if( idOrNew === 'new') {
        return WorkshopSettingsModalChild({ title: 'Create new Workshop', showUserFeedback, submitEventHandler});
    }
        
    return WorkshopSettingsModalChild({ title: 'Modify workshop settings', id: idOrNew, showUserFeedback, submitEventHandler});
}

const WorkshopSettingsModalChild = ({ title, id, showUserFeedback, submitEventHandler }: WorkshopSettingsModalProps) => {
    const [workshopSettings, setWorkshopSettings] = React.useState<WorkshopSettings | undefined>(undefined);

    const [workshopInterfaces, setWorkshopInterfaces] = React.useState<Interface[]>([]);

    const [selectedInterfaces, setSelectedInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [oldselectedInterfaces, setOldSelectedInterfaces] = React.useState<WorkshopInterface[]>([]);

    const [workshopCreator, setWorkshopCreator] = React.useState<any>(null);

    const userContext = useContext(UserContext);
    const navigate = useNavigate();

    const fetch = React.useCallback(async (id: number) => {
        try {
            const interfacesResponse = await httpGet('/api/interface', new AbortController());
            const workshopInterfacesLoc = interfacesResponse.result;
            setWorkshopInterfaces(workshopInterfacesLoc);

            if (id !== 0) {
                let promiseResults: Promise<any>[] = [];
                const sharedAbortController = new AbortController();

                const workshopResponse = httpGet(`/api/workshop/get/${id}`, sharedAbortController);
                promiseResults.push(workshopResponse);

                const workshopInterfacesResponse = httpGet(`/api/workshop/interface/${id}`, sharedAbortController);
                promiseResults.push(workshopInterfacesResponse);

                const responses = await Promise.all(promiseResults);

                let someFailed = responses.find(response => response.failed);

                if (someFailed) {
                    showUserFeedback(someFailed.errorText);
                    return;
                }

                const workshopSettingsLoc = (await workshopResponse).result;
                if (workshopSettingsLoc.creatorId) {
                    const creatorResponse = await httpGet(`/api/user/get/${workshopSettingsLoc.creatorId}`, new AbortController());
                    if (creatorResponse.failed) {
                        showUserFeedback(creatorResponse.errorText);
                        return;
                    }
                    setWorkshopCreator(creatorResponse.result);
                }
                else setWorkshopCreator(null);
                setWorkshopSettings(workshopSettingsLoc);

                const selectedInterfacesLoc = (await workshopInterfacesResponse).result;
                setSelectedInterfaces(selectedInterfacesLoc);
                setOldSelectedInterfaces(selectedInterfacesLoc);
            }
            else {
                setWorkshopSettings(wssettings => ({...wssettings, creatorId: userContext.user.id}));
                setWorkshopCreator(userContext.user);
            }

        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [showUserFeedback, userContext.user]);

    React.useEffect(() => {
        if (id !== undefined) {
            fetch(Number(id));
        }
        else {
            fetch(0);
        }
    }, [fetch, id]);

    const statusOptions = ["Created", "Pre Registration", "Registration", "Testing", "Closed", "Invisible"];
    const paymentTypeOptions = ["Not required", "Credit Card"];

    const handleClose = () => {
        navigate(-1);
    }

    return <Modal title={title}>
        <Typography variant={'body2'} sx={{marginBottom: 20}}>
        Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis, ultricies nec, pellentesque eu, pretium quis, sem. Nulla consequat massa quis enim.
        </Typography>

        <form onSubmit={submitEventHandler(workshopSettings, selectedInterfaces, oldselectedInterfaces)}>
        <Stack spacing={20}  divider={<Divider orientation='horizontal' flexItem />}>
            <Grid container rowSpacing={30} columnSpacing={30}>
                <Grid size={{ xs: 6 }}>
                    <FormControl required fullWidth>
                        <InputLabel htmlFor="ws-name">{"Workshop Name"}</InputLabel>
                        <OutlinedInput notched name='name' id="ws-name" label={"Workshop Name"} value={workshopSettings?.name}
                            defaultValue={'Please Enter a name for the Workshop'}
                            onChange={(e) => setWorkshopSettings({ ...workshopSettings, name: e.target.value })}
                            onFocus={(e) => e.target.value = ""}
                            sx={{
                                "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "black",
                                },
                            }}
                        />
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 3 }}>
                    <FormControl required >
                            <DatePicker label={"From Date"} views={['year', 'month', 'day']}
                                format='YYYY-MM-DD' value={dayjs(workshopSettings?.startDate)}
                                onChange={(newValue) => setWorkshopSettings({ ...workshopSettings, startDate: dayjs(newValue).format('YYYYMMDD000000') })}
                                slotProps={{
                                    textField: {
                                        name: 'fromDate',
                                        helperText: 'YYYY-MM-DD',
                                        sx:
                                        {
                                            "&.MuiTextField-root .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "black",
                                            },
                                            "&.MuiTextField-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "black",
                                            },
                                        },
                                },
                                }}
                            />
                    </FormControl>
                </Grid>
                
                <Grid size={{ xs: 3 }}>
                    <FormControl required>
                        <DatePicker label={"To Date"} views={['year', 'month', 'day']}
                                    format='YYYY-MM-DD' value={dayjs(workshopSettings?.endDate)}
                                    onChange={(newValue)=>setWorkshopSettings({...workshopSettings, endDate: dayjs(newValue).format('YYYYMMDD000000')})}
                            slotProps={{
                                textField: {
                                    name: 'toDate',
                                    helperText: 'YYYY-MM-DD',
                                    sx:
                                    {
                                        "&.MuiTextField-root .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                        "&.MuiTextField-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                    },
                                },
                            }}/>
                    </FormControl>
                </Grid>
                
                <Grid size={{ xs: 6 }}>
                    <FormControl required fullWidth>
                            <InputLabel htmlFor='ws-location'>{"Location"}</InputLabel>
                            <OutlinedInput notched name='location' id='ws-location' label={"Location"} value={workshopSettings?.location}
                                defaultValue={'Where will the Workshop be held?'}
                                onChange={(e) => setWorkshopSettings({ ...workshopSettings, location: e.target.value })}
                                onFocus={(e) => e.target.value = ""}
                                sx={{
                                    "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "black",
                                    },
                                }}
                            />
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 3 }}>
                    <FormControl required fullWidth>
                        <Autocomplete id='ws-status' options={statusOptions}
                            value={statusOptions[(workshopSettings?.status) ?? 0]}
                            onChange={(event, value)=>setWorkshopSettings({...workshopSettings, status: statusOptions.indexOf(value ?? statusOptions[0])})}
                            renderInput={(params) =>
                                <TextField name='status' multiline
                                    {...params}
                                    label={"Status"}
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root fieldset': {
                                            borderColor: 'divider',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>
                
                <Grid size={{ xs: 3 }}>
                    <FormControl required>
                        <InputLabel htmlFor='ws-users'>{"Max users"}</InputLabel>
                            <OutlinedInput notched name='maxUsers' id='ws-users' label={"Max users"} value={workshopSettings?.maxUsers}
                                defaultValue={'Maximum User?'}
                                onChange={(e) => setWorkshopSettings({ ...workshopSettings, maxUsers: Number(e.target.value) })}
                                onFocus={(e) => e.target.value = ""}
                                sx={{
                                    "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "black",
                                    },
                                }}
                            />
                    </FormControl>
                </Grid>    

                <Grid size={{ xs: 6 }}>
                    <FormControl disabled fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='ws-creator'>{"Creator (auto)"}</InputLabel>
                        <OutlinedInput notched name='creator' id='ws-creator' label={"Creator (auto)"} value={workshopCreator?.name}/>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 3 }}>
                    <FormControl required fullWidth>
                        <Autocomplete id='ws-paymentType' options={paymentTypeOptions}
                            value={paymentTypeOptions[(workshopSettings?.paymentType) ?? 0]}
                            onChange={(e, value)=> setWorkshopSettings({ ...workshopSettings, paymentType: paymentTypeOptions.indexOf(value as string) })}
                            renderInput={(params) =>
                                <TextField name='paymentType' multiline
                                    {...params}
                                    label={"Payment Type"}
                                    sx={{
                                        '& .MuiOutlinedInput-root fieldset': {
                                            borderColor: 'divider',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 3 }}>
                    <FormControl required>
                        <InputLabel htmlFor='ws-fee'>{"Fee (string)"}</InputLabel>
                        <OutlinedInput notched name='fee' id='ws-fee' label={"Fee (string)"} value={workshopSettings?.fee}
                            defaultValue="$0.00 USD"
                            onChange={(e) => setWorkshopSettings({ ...workshopSettings, fee: e.target.value })}
                            onFocus={(e) => e.target.value = ""}
                            sx={{
                                "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "black",
                                },
                            }}
                        />
                    </FormControl>
                </Grid>
                
                <Grid size={{ xs: 6 }}>
                    <FormControl required fullWidth>
                        <InputLabel htmlFor='ws-email'>{"Contact (Email)"}</InputLabel>
                        <OutlinedInput notched name='contactEmail' id='ws-email' label={"Contact (Email)"} value={workshopSettings?.contactEmail}
                            defaultValue="example@domain.com"
                            onChange={(e) => setWorkshopSettings({ ...workshopSettings, contactEmail: e.target.value })}
                            onFocus={(e) => e.target.value = ""}
                            sx={{
                                "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "black",
                                },
                            }}
                        />
                    </FormControl>
                </Grid>

                    <Grid size={{ xs: 6 }}>
                        {
                            workshopSettings?.guid && <FormControl disabled fullWidth variant="outlined">
                                <InputLabel shrink htmlFor='ws-guid'>{"Workshop ID"}</InputLabel>
                                <OutlinedInput notched name='guid' id='ws-guid' label={"Workshop ID"} value={workshopSettings?.guid}/>
                            </FormControl>
                        }
                </Grid>

                <Grid size={{ xs: 6 }}>
                    <FormControl sx={{width: "100%"}}>
                        <InputLabel shrink htmlFor='ws-announcement'>{"General information (Full URL)"}</InputLabel>
                        <OutlinedInput notched name='informationUrl' id='ws-announcement' label={"General information (Full URL)"} value={workshopSettings?.informationUrl}
                            defaultValue="http://www."
                        onChange={(e) => setWorkshopSettings({ ...workshopSettings, informationUrl: e.target.value })}
                            sx={{
                                "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "black",
                                },
                            }}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            
            <Grid container size={{ xs: 12 }} sx={{width: '500'}} paddingBottom={20}>
                <InterfaceSearchAutocomplete wsId={workshopSettings?.id as number} available={workshopInterfaces.filter(iface => iface.status !== InterfaceStatus.Disabled)}
                    selected={selectedInterfaces} setNewSelected={setSelectedInterfaces} />
            </Grid>
        </Stack>

        <Stack direction='row' justifyContent='space-between'>
            <Button onClick={handleClose} startIcon={<CancelIcon/>}>{"Cancel"}</Button>
            <Button type='submit' startIcon={<AutorenewIcon/>}>{"Save Changes"}</Button>
        </Stack>
        </form>
    </Modal>
}
